"use client";

import { c as _c } from "react/compiler-runtime";
import { ImagePlus, Loader2, Sparkles, X, Zap } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { AspectRatioIcon } from "@/components/playground/aspect-ratio-icon";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getModelImageConfig } from "@/lib/image-gen";
import { cn } from "@/lib/utils";
import type { AspectRatio } from "@/lib/image-gen";
export type ImageServiceTier = "default" | "flex";
interface InputImage {
  dataUrl: string;
  mediaType: string;
}
interface ImageControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  imageAspectRatio: AspectRatio;
  setImageAspectRatio: (value: AspectRatio) => void;
  imageSize: string;
  setImageSize: (value: string) => void;
  alibabaImageSize: string;
  setAlibabaImageSize: (value: string) => void;
  imageQuality: string;
  setImageQuality: (value: string) => void;
  imageModeration: string;
  setImageModeration: (value: string) => void;
  imageCount: 1 | 2 | 3 | 4;
  setImageCount: (value: 1 | 2 | 3 | 4) => void;
  serviceTier: ImageServiceTier;
  setServiceTier: (value: ImageServiceTier) => void;
  // Whether any model in the current selection offers the flex tier.
  supportsFlex: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  isEditModel: boolean;
  requiresImageInput: boolean;
  inputImages: InputImage[];
  setInputImages: Dispatch<SetStateAction<InputImage[]>>;
}
const aspectRatios: AspectRatio[] = ["auto", "1:1", "9:16", "16:9", "3:4", "4:3", "3:2", "2:3", "5:4", "4:5", "21:9", "1:4", "4:1", "1:8", "8:1"];
export function ImageControls(t0) {
  const $ = _c(124);
  const {
    prompt,
    setPrompt,
    selectedModels,
    imageAspectRatio,
    setImageAspectRatio,
    imageSize,
    setImageSize,
    alibabaImageSize,
    setAlibabaImageSize,
    imageQuality,
    setImageQuality,
    imageModeration,
    setImageModeration,
    imageCount,
    setImageCount,
    serviceTier,
    setServiceTier,
    supportsFlex,
    isGenerating,
    onGenerate,
    isEditModel,
    requiresImageInput,
    inputImages,
    setInputImages
  } = t0;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const primaryModel = selectedModels[0] ?? "";
  const config = getModelImageConfig(primaryModel);
  const maxInputImages = config.maxInputImages;
  const filteredAspectRatios = config.supportedAspectRatios ?? aspectRatios;
  useEffect(() => {
    if (config.supportedAspectRatios && !config.supportedAspectRatios.includes(imageAspectRatio)) {
      setImageAspectRatio("auto");
    }
  }, [config.supportedAspectRatios, imageAspectRatio, setImageAspectRatio]);
  let t1;
  if ($[0] !== isEditModel || $[1] !== maxInputImages || $[2] !== setInputImages) {
    t1 = file => {
      if (!file.type.startsWith("image/")) {
        return;
      }
      if (!isEditModel) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setInputImages(prev => {
          if (prev.length >= maxInputImages) {
            return prev;
          }
          return [...prev, {
            dataUrl: reader.result as string,
            mediaType: file.type
          }];
        });
      };
      reader.readAsDataURL(file);
    };
    $[0] = isEditModel;
    $[1] = maxInputImages;
    $[2] = setInputImages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addImageFile = t1;
  let t2;
  if ($[4] !== isGenerating || $[5] !== onGenerate || $[6] !== prompt) {
    t2 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (prompt.trim() && !isGenerating && canGenerate) {
          onGenerate();
        }
      }
    };
    $[4] = isGenerating;
    $[5] = onGenerate;
    $[6] = prompt;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[8] !== addImageFile || $[9] !== inputImages.length || $[10] !== maxInputImages) {
    t3 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (inputImages.length >= maxInputImages) {
          break;
        }
        addImageFile(file_0);
      }
      e_0.target.value = "";
    };
    $[8] = addImageFile;
    $[9] = inputImages.length;
    $[10] = maxInputImages;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleFileSelect = t3;
  let t4;
  if ($[12] !== addImageFile || $[13] !== inputImages.length || $[14] !== isEditModel || $[15] !== maxInputImages) {
    t4 = e_1 => {
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[12] = addImageFile;
    $[13] = inputImages.length;
    $[14] = isEditModel;
    $[15] = maxInputImages;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handlePaste = t4;
  let t5;
  if ($[17] !== inputImages.length || $[18] !== isEditModel || $[19] !== maxInputImages) {
    t5 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (isEditModel && inputImages.length < maxInputImages) {
        setIsDragging(true);
      }
    };
    $[17] = inputImages.length;
    $[18] = isEditModel;
    $[19] = maxInputImages;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleDragOver = t5;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleDragLeave = t6;
  let t7;
  if ($[22] !== addImageFile || $[23] !== inputImages.length || $[24] !== isEditModel || $[25] !== maxInputImages) {
    t7 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2);
        }
      }
    };
    $[22] = addImageFile;
    $[23] = inputImages.length;
    $[24] = isEditModel;
    $[25] = maxInputImages;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleDrop = t7;
  let t8;
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t9 = [];
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== maxInputImages || $[30] !== setInputImages) {
    t10 = () => {
      setInputImages(prev_0 => prev_0.length > maxInputImages ? prev_0.slice(0, maxInputImages) : prev_0);
    };
    t11 = [maxInputImages, setInputImages];
    $[29] = maxInputImages;
    $[30] = setInputImages;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  if ($[33] !== setInputImages) {
    t12 = index => {
      setInputImages(prev_1 => prev_1.filter((_, i) => i !== index));
    };
    $[33] = setInputImages;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const removeImage = t12;
  const canGenerate = prompt.trim().length > 0 && selectedModels.length > 0;
  const t13 = "border-b bg-background p-4";
  const t14 = "max-w-4xl mx-auto space-y-3";
  const t15 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t16;
  if ($[35] !== inputImages.length || $[36] !== isDragging || $[37] !== isEditModel || $[38] !== maxInputImages) {
    t16 = isDragging && isEditModel && inputImages.length < maxInputImages && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="h-4 w-4 mr-2" />Drop image here</div>;
    $[35] = inputImages.length;
    $[36] = isDragging;
    $[37] = isEditModel;
    $[38] = maxInputImages;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== inputImages || $[41] !== isEditModel || $[42] !== removeImage) {
    t17 = isEditModel && inputImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{inputImages.map((img, i_0) => <div key={i_0} className="relative group h-14 w-14 rounded-md border"><img src={img.dataUrl} alt={`Input ${i_0 + 1}`} className="size-full rounded-md object-cover" /><button type="button" aria-label="Remove attachment" onClick={() => removeImage(i_0)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[40] = inputImages;
    $[41] = isEditModel;
    $[42] = removeImage;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== setPrompt) {
    t18 = e_5 => setPrompt(e_5.target.value);
    $[44] = setPrompt;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = requiresImageInput ? "Describe how to edit the image... (paste or drop an image)" : isEditModel ? "Describe the image you want to generate... (optionally paste or drop an image)" : "Describe the image you want to generate...";
  let t20;
  if ($[46] !== handleKeyDown || $[47] !== handlePaste || $[48] !== isGenerating || $[49] !== prompt || $[50] !== t18 || $[51] !== t19) {
    t20 = <Textarea ref={textareaRef} value={prompt} onChange={t18} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={t19} className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 shadow-none" disabled={isGenerating} />;
    $[46] = handleKeyDown;
    $[47] = handlePaste;
    $[48] = isGenerating;
    $[49] = prompt;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== handleDragLeave || $[54] !== handleDragOver || $[55] !== handleDrop || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t20) {
    t21 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={t15}>{t16}{t17}{t20}</div>;
    $[53] = handleDragLeave;
    $[54] = handleDragOver;
    $[55] = handleDrop;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== handleFileSelect) {
    t22 = <input ref={fileInputRef} type="file" accept="image/*" multiple={true} className="hidden" onChange={handleFileSelect} />;
    $[61] = handleFileSelect;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const t23 = "flex flex-wrap items-center gap-2";
  let t24;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => fileInputRef.current?.click();
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  const t25 = isGenerating || !isEditModel || inputImages.length >= maxInputImages;
  const t26 = !isEditModel ? "Image input not supported by selected model" : undefined;
  let t27;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ImagePlus className="h-4 w-4 mr-1.5" />;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = inputImages.length === 0 ? "Add image" : `${inputImages.length}/${maxInputImages}`;
  let t29;
  if ($[65] !== t25 || $[66] !== t26 || $[67] !== t28) {
    t29 = <Button variant="outline" size="sm" onClick={t24} disabled={t25} title={t26}>{t27}{t28}</Button>;
    $[65] = t25;
    $[66] = t26;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  const t30 = !config.usesPixelDimensions && <><Select value={imageAspectRatio} onValueChange={val => setImageAspectRatio(val as AspectRatio)}><SelectTrigger size="sm" className="min-w-[110px]"><SelectValue placeholder="Aspect ratio" /></SelectTrigger><SelectContent>{filteredAspectRatios.map(_temp)}</SelectContent></Select>{!config.isReve && <Select value={imageSize} onValueChange={setImageSize}><SelectTrigger size="sm" className="min-w-[80px]"><SelectValue placeholder="Resolution" /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp2)}</SelectContent></Select>}</>;
  let t31;
  if ($[69] !== alibabaImageSize || $[70] !== config.availableSizes || $[71] !== config.isGptImage || $[72] !== config.usesPixelDimensions || $[73] !== setAlibabaImageSize) {
    t31 = config.usesPixelDimensions && config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder="Resolution" /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp3)}</SelectContent></Select>;
    $[69] = alibabaImageSize;
    $[70] = config.availableSizes;
    $[71] = config.isGptImage;
    $[72] = config.usesPixelDimensions;
    $[73] = setAlibabaImageSize;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== alibabaImageSize || $[76] !== config.availableSizes || $[77] !== config.isGptImage || $[78] !== config.usesPixelDimensions || $[79] !== setAlibabaImageSize) {
    t32 = config.usesPixelDimensions && !config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder="Image Size" /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp4)}</SelectContent></Select>;
    $[75] = alibabaImageSize;
    $[76] = config.availableSizes;
    $[77] = config.isGptImage;
    $[78] = config.usesPixelDimensions;
    $[79] = setAlibabaImageSize;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== config.availableQualities || $[82] !== config.supportsQuality || $[83] !== imageQuality || $[84] !== setImageQuality) {
    t33 = config.supportsQuality && <Select value={imageQuality} onValueChange={setImageQuality}><SelectTrigger size="sm" className="min-w-[100px]"><SelectValue placeholder="Quality" /></SelectTrigger><SelectContent>{config.availableQualities.map(_temp5)}</SelectContent></Select>;
    $[81] = config.availableQualities;
    $[82] = config.supportsQuality;
    $[83] = imageQuality;
    $[84] = setImageQuality;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== config.availableModerations || $[87] !== config.supportsModeration || $[88] !== imageModeration || $[89] !== setImageModeration) {
    t34 = config.supportsModeration && <Select value={imageModeration} onValueChange={setImageModeration}><SelectTrigger size="sm" className="min-w-[150px]"><SelectValue placeholder="Moderation" /></SelectTrigger><SelectContent>{config.availableModerations.map(_temp6)}</SelectContent></Select>;
    $[86] = config.availableModerations;
    $[87] = config.supportsModeration;
    $[88] = imageModeration;
    $[89] = setImageModeration;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const t35 = String(imageCount);
  let t36;
  if ($[91] !== setImageCount) {
    t36 = val_0 => setImageCount(Number(val_0) as 1 | 2 | 3 | 4);
    $[91] = setImageCount;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder="Count" /></SelectTrigger>;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <SelectContent><SelectItem value="1">1 image</SelectItem><SelectItem value="2">2 images</SelectItem><SelectItem value="3">3 images</SelectItem><SelectItem value="4">4 images</SelectItem></SelectContent>;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t35 || $[96] !== t36) {
    t39 = <Select value={t35} onValueChange={t36}>{t37}{t38}</Select>;
    $[95] = t35;
    $[96] = t36;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] !== isGenerating || $[99] !== serviceTier || $[100] !== setServiceTier || $[101] !== supportsFlex) {
    t40 = supportsFlex && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><Button type="button" variant={serviceTier === "flex" ? "secondary" : "outline"} size="sm" aria-pressed={serviceTier === "flex"} disabled={isGenerating} onClick={() => setServiceTier(serviceTier === "flex" ? "default" : "flex")}><Zap className={cn("h-4 w-4 mr-1.5", serviceTier === "flex" && "text-lounge-gold fill-lounge-gold")} />Flex</Button></TooltipTrigger><TooltipContent className="max-w-xs">Flex processing costs about half as much, but runs slower and is best-effort under load. Applies to the selected models that support it.</TooltipContent></Tooltip></TooltipProvider>;
    $[98] = isGenerating;
    $[99] = serviceTier;
    $[100] = setServiceTier;
    $[101] = supportsFlex;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="flex-1" />;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  const t42 = isGenerating || !canGenerate;
  let t43;
  if ($[104] !== isGenerating) {
    t43 = isGenerating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate</>;
    $[104] = isGenerating;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== onGenerate || $[107] !== t42 || $[108] !== t43) {
    t44 = <Button onClick={onGenerate} disabled={t42} className="min-w-[120px]">{t43}</Button>;
    $[106] = onGenerate;
    $[107] = t42;
    $[108] = t43;
    $[109] = t44;
  } else {
    t44 = $[109];
  }
  let t45;
  if ($[110] !== t29 || $[111] !== t30 || $[112] !== t31 || $[113] !== t32 || $[114] !== t33 || $[115] !== t34 || $[116] !== t39 || $[117] !== t40 || $[118] !== t44) {
    t45 = <div className={t23}>{t29}{t30}{t31}{t32}{t33}{t34}{t39}{t40}{t41}{t44}</div>;
    $[110] = t29;
    $[111] = t30;
    $[112] = t31;
    $[113] = t32;
    $[114] = t33;
    $[115] = t34;
    $[116] = t39;
    $[117] = t40;
    $[118] = t44;
    $[119] = t45;
  } else {
    t45 = $[119];
  }
  let t46;
  if ($[120] !== t21 || $[121] !== t22 || $[122] !== t45) {
    t46 = <div className={t13}><div className={t14}>{t21}{t22}{t45}</div></div>;
    $[120] = t21;
    $[121] = t22;
    $[122] = t45;
    $[123] = t46;
  } else {
    t46 = $[123];
  }
  return t46;
}
function _temp6(m) {
  return <SelectItem key={m} value={m}>Moderation: {m}</SelectItem>;
}
function _temp5(q) {
  return <SelectItem key={q} value={q}>{q.charAt(0).toUpperCase() + q.slice(1)}</SelectItem>;
}
function _temp4(size_1) {
  return <SelectItem key={size_1} value={size_1}>{size_1}</SelectItem>;
}
function _temp3(size_0) {
  return <SelectItem key={size_0} value={size_0}>{size_0 === "auto" ? "Auto" : size_0}</SelectItem>;
}
function _temp2(size) {
  return <SelectItem key={size} value={size}>{size}</SelectItem>;
}
function _temp(r) {
  return <SelectItem key={r} value={r}><span className="flex items-center gap-2"><AspectRatioIcon ratio={r} />{r === "auto" ? "Auto" : r}</span></SelectItem>;
}